import { UserPermissions } from "@redotech/account-common/permissions";
import { ManagedUserRoles } from "@redotech/account-common/roles";
import { TeamType as AccountTeamType } from "@redotech/account-common/team-type";
import { GetUser } from "@redotech/account-common/user";
import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { SupportAiBillingVersion } from "@redotech/redo-billing-common/support-ai-billing-version";
import { SupportBaseBillingVersion } from "@redotech/redo-billing-common/team-billing";
import { RedoPlatformRpcClient } from "@redotech/redo-merchant-platform-rpc/client";
import { FeatureOnboardingType } from "@redotech/redo-model/onboarding/user-onboarding";
import { teamCanUseAi } from "@redotech/redo-model/team";
import { TeamContextValue } from "@redotech/redo-web/context/team-context";
import { isWmsRoute } from "../app/wms-route";
import { isBeforeGivingDayCutoff } from "./giving-day-teaser-window";

type TeamType = NonNullable<TeamContextValue>;

export interface FeatureOnboardingDefinition {
  type: FeatureOnboardingType;
  isEligible: (
    team: TeamType,
    user: GetUser | undefined,
    platformRpcClient: RedoPlatformRpcClient | undefined,
    permissions: Set<UserPermissions> | undefined,
  ) => Promise<boolean>;
  priority: number;
  retries?: number;
}

export async function isEligibleForConversion(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return (
    !team.settings.checkoutOptimization?.enabled && team.platform === "shopify"
  );
}

export async function isEligibleForPackageProtectionPlus(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return (
    team.settings.crossSellPopoutEnabled === true &&
    !team.settings.packageProtection?.packageProtectionPlusEnabled
  );
}

export async function isEligibleForExpandedWarranty(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return (
    team.settings.warranties?.enabled === true &&
    !team.settings.extendedWarranties?.enabled
  );
}

export async function isEligibleForManagedReturns(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return team.settings.returns?.enabled === true;
}

export async function isEligibleForSupportToAiUpsell(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  // Check if current route includes "home" or "support"
  const currentPath = window.location.pathname;
  const shouldShowOnRoute = currentPath.includes("support");

  if (!shouldShowOnRoute) {
    return false;
  }

  const isPerOrderBilling =
    team.settings.support?.billing?.base?.version ===
      SupportBaseBillingVersion.PER_ORDER ||
    team.settings.support?.billing?.ai?.version ===
      SupportAiBillingVersion.PER_ORDER;

  if (isPerOrderBilling) {
    return false;
  }

  const isFreeCustomer =
    team.settings.support?.billing?.base?.pricePerMonthInCents === 0 &&
    team.settings.support?.billing?.base?.version !==
      SupportBaseBillingVersion.BUCKETED_USAGE;

  const isAiBillingAccepted =
    team.settings.support?.billing?.ai?.billingAccepted;

  return (
    team.settings.support?.enabled === true &&
    isFreeCustomer &&
    !isAiBillingAccepted
  );
}

export async function isEligibleForMarketingTexasSmsUpdate(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return Boolean(
    team.settings.marketing?.enabled && team.settings.marketing.textsEnabled,
  );
}

export async function isEligibleForIntercomMoved(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  // This was a one-time notification about the chat widget moving
  // Since it's historical, return false to prevent showing to new users
  return false;
}

export async function isEligibleForBlackFridayTips(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return !!team.settings.marketing?.enabled;
}

export async function isEligibleForShopperAiWrapped(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  // Only show until January 1, 2026
  const expirationDate = Temporal.PlainDate.from("2026-01-01");
  const today = Temporal.Now.plainDateISO();
  if (Temporal.PlainDate.compare(today, expirationDate) >= 0) {
    return false;
  }
  return teamCanUseAi(team);
}

export async function isEligibleForHomepageAnalyticsMoved(
  team: TeamType,
): Promise<boolean> {
  const isOnSummaryPage = window.location.pathname.includes("/summary");
  if (!isOnSummaryPage) {
    return false;
  }
  return Boolean(
    team.settings.marketing?.enabled ||
    team.settings.orderTracking?.enabled ||
    team.settings.support?.enabled,
  );
}

export async function isEligibleForReclaimAnnouncement(
  team: TeamType,
): Promise<boolean> {
  return !team.settings.disputes?.automateEvidenceEnabled;
}

const SUPPORT_V2_FIRST_SEEN_KEY = "redo.support-v2-first-seen-at";
const SUPPORT_V2_MODAL_DELAY_HOURS = 48;

export async function isEligibleForSupportV2NewLook(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  const supportV2BasePath = `/stores/${String(team._id)}/support-v2`;
  if (!window.location.pathname.startsWith(supportV2BasePath)) {
    return false;
  }
  if (!platformRpcClient) {
    return false;
  }
  try {
    const response = await platformRpcClient.getFeatureFlag({
      flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2,
    });
    if (!response.enabled) {
      return false;
    }
  } catch {
    return false;
  }

  try {
    const storedAt = localStorage.getItem(SUPPORT_V2_FIRST_SEEN_KEY);
    if (!storedAt) {
      localStorage.setItem(
        SUPPORT_V2_FIRST_SEEN_KEY,
        Temporal.Now.instant().toString(),
      );
      return false;
    }

    const firstSeenAt = Temporal.Instant.from(storedAt);
    const hoursSinceFirstSeen = Temporal.Now.instant()
      .since(firstSeenAt)
      .total({ unit: "hours" });
    return hoursSinceFirstSeen >= SUPPORT_V2_MODAL_DELAY_HOURS;
  } catch {
    return false;
  }
}

export async function isEligibleForFeedbackAnnouncement(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
  permissions: Set<UserPermissions> | undefined,
): Promise<boolean> {
  // Suppress for automated browser sessions (Playwright/Selenium/etc.) so
  // the top-right card doesn't intercept clicks on right-aligned action
  // buttons (Save changes, Order actions, etc.) during E2E tests.
  // navigator.webdriver is a standard WebDriver flag set by Playwright.
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  return true;
}

export async function isEligibleForGivingDayTeaser(): Promise<boolean> {
  return isBeforeGivingDayCutoff(Temporal.Now.instant());
}

export async function isEligibleForShippingClaimsAutoFiling(
  team: TeamType,
  user: GetUser | undefined,
): Promise<boolean> {
  if (!user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN)) {
    return false;
  }
  if (!window.location.pathname.includes("shipping")) {
    return false;
  }
  if (team.settings.outboundLabels?.claimSubmission?.enabled) {
    return false;
  }
  if (!team.settings.outboundLabels?.newOmsEnabled) {
    return false;
  }
  return true;
}

export function isRedoEmployeeEmail(email: string | undefined): boolean {
  const domain = email?.split("@").at(-1)?.toLowerCase();
  return domain === "redo.com" || domain === "getredo.com";
}

export async function isEligibleForWithdrawalsOnboarding(
  team: TeamType,
  user: GetUser | undefined,
): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  return Boolean(user?.email) && !isRedoEmployeeEmail(user?.email);
}

export async function isEligibleForRedo3plInterestSurvey(
  team: TeamType,
  _user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  if (isWmsRoute(window.location.pathname)) {
    return false;
  }

  // Already-3PL teams should not see a 3PL interest survey.
  if (team.teamType === AccountTeamType.FulfillmentService) {
    return false;
  }

  // Brands connected to a 3PL should not see this survey either.
  if (team.fulfillmentServices.length > 0) {
    return false;
  }

  if (!platformRpcClient) {
    return false;
  }

  try {
    const response = await platformRpcClient.getUserFeatureFlag({
      flagKey: FeatureFlagKey.REDO_3PL_INTEREST_SURVEY,
    });
    return response.enabled;
  } catch {
    return false;
  }
}

/**
 * Users will get exposed to the onboarding retries + 1 times. (if it isn't completed)
 */
export const featureOnboardingDefinitions: Record<
  Exclude<FeatureOnboardingType, FeatureOnboardingType.DISABLED>,
  FeatureOnboardingDefinition
> = {
  // Historical onboarding types - no longer actively shown
  [FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED]: {
    type: FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.HELP_ICON_MOVING]: {
    type: FeatureOnboardingType.HELP_ICON_MOVING,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.AGENTIC_CATALOG]: {
    type: FeatureOnboardingType.AGENTIC_CATALOG,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.INTERCOM_MOVED]: {
    type: FeatureOnboardingType.INTERCOM_MOVED,
    isEligible: isEligibleForIntercomMoved,
    priority: 0,
    retries: 0,
  },
  [FeatureOnboardingType.CONVERSION]: {
    type: FeatureOnboardingType.CONVERSION,
    isEligible: isEligibleForConversion,
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.MANAGED_RETURNS]: {
    type: FeatureOnboardingType.MANAGED_RETURNS,
    isEligible: isEligibleForManagedReturns,
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.PACKAGE_PROTECTION_PLUS]: {
    type: FeatureOnboardingType.PACKAGE_PROTECTION_PLUS,
    isEligible: isEligibleForPackageProtectionPlus,
    priority: 4,
    retries: 1,
  },
  [FeatureOnboardingType.EXPANDED_WARRANTY]: {
    type: FeatureOnboardingType.EXPANDED_WARRANTY,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.SUPPORT_TO_AI_UPSELL]: {
    type: FeatureOnboardingType.SUPPORT_TO_AI_UPSELL,
    isEligible: isEligibleForSupportToAiUpsell,
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.COMMENTSOLD_REDO]: {
    type: FeatureOnboardingType.COMMENTSOLD_REDO,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.SHOPPER_AI_WRAPPED]: {
    type: FeatureOnboardingType.SHOPPER_AI_WRAPPED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.DISPUTES_WELCOME]: {
    type: FeatureOnboardingType.DISPUTES_WELCOME,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.RECLAIM_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.RECLAIM_ANNOUNCEMENT,
    isEligible: isEligibleForReclaimAnnouncement,
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED]: {
    type: FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.NAV_REORGANIZATION]: {
    type: FeatureOnboardingType.NAV_REORGANIZATION,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.SUPPORT_V2_NEW_LOOK]: {
    type: FeatureOnboardingType.SUPPORT_V2_NEW_LOOK,
    isEligible: isEligibleForSupportV2NewLook,
    priority: 2,
    retries: 1,
  },
  [FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.WITHDRAWALS_ONBOARDING]: {
    type: FeatureOnboardingType.WITHDRAWALS_ONBOARDING,
    isEligible: isEligibleForWithdrawalsOnboarding,
    priority: -1,
    retries: 0,
  },
  [FeatureOnboardingType.REDO_3PL_INTEREST_SURVEY]: {
    type: FeatureOnboardingType.REDO_3PL_INTEREST_SURVEY,
    isEligible: isEligibleForRedo3plInterestSurvey,
    priority: 2,
    retries: 0,
  },
  [FeatureOnboardingType.GIVING_DAY_TEASER]: {
    type: FeatureOnboardingType.GIVING_DAY_TEASER,
    isEligible: isEligibleForGivingDayTeaser,
    priority: 0,
    retries: 0,
  },
  [FeatureOnboardingType.SHIPPING_CLAIMS_AUTO_FILING]: {
    type: FeatureOnboardingType.SHIPPING_CLAIMS_AUTO_FILING,
    isEligible: isEligibleForShippingClaimsAutoFiling,
    priority: 3,
    retries: 1,
  },
  // Historical onboarding types - no longer actively used but exist in MongoDB data
  [FeatureOnboardingType.CROSS_BORDER_ONBOARDING]: {
    type: FeatureOnboardingType.CROSS_BORDER_ONBOARDING,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_VIEWS_MOVED]: {
    type: FeatureOnboardingType.OUTBOUND_VIEWS_MOVED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL]: {
    type: FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.INTERNATIONAL_RETURNS]: {
    type: FeatureOnboardingType.INTERNATIONAL_RETURNS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE]: {
    type: FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION]: {
    type: FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.BLACK_FRIDAY_TIPS]: {
    type: FeatureOnboardingType.BLACK_FRIDAY_TIPS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.PERMISSIONS_ROLES]: {
    type: FeatureOnboardingType.PERMISSIONS_ROLES,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
};
